import { useCallback } from 'react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'

type ChangelogEntry = { id: string; title: string }
type ChangelogCta = { id: string; label: string; url: string }
type ChangelogDisplayType = 'popover' | 'modal'

/**
 * Hook for tracking changelog-related analytics events.
 * Follows the same pattern as useCta hook.
 */
export function useChangelogTracking(): {
  trackChangelogEntryViewed: (entry: ChangelogEntry) => void
  trackChangelogEntryDismissed: (entry: ChangelogEntry) => void
  trackChangelogEntryListViewed: () => void
  trackChangelogEntryCtaClicked: (
    entry: ChangelogEntry,
    cta: ChangelogCta,
    displayType: ChangelogDisplayType,
  ) => void
} {
  const { account } = useAccount()
  const organizationId = useOrganizationId()

  const trackChangelogEntryViewed = useCallback(
    (entry: ChangelogEntry): void => {
      if (!account?.id || !organizationId) return

      BufferTracker.changelogEntryViewed({
        userId: account.id,
        accountId: account.id,
        organizationId,
        changelogEntryId: entry.id,
        changelogEntryTitle: entry.title,
        clientName: Client.PublishWeb,
      })
    },
    [account?.id, organizationId],
  )

  const trackChangelogEntryDismissed = useCallback(
    (entry: ChangelogEntry): void => {
      if (!account?.id || !organizationId) return

      BufferTracker.changelogEntryDismissed({
        userId: account.id,
        accountId: account.id,
        organizationId,
        changelogEntryId: entry.id,
        changelogEntryTitle: entry.title,
        clientName: Client.PublishWeb,
      })
    },
    [account?.id, organizationId],
  )

  const trackChangelogEntryListViewed = useCallback((): void => {
    if (!account?.id || !organizationId) return

    BufferTracker.changelogEntryListViewed({
      userId: account.id,
      accountId: account.id,
      organizationId,
      clientName: Client.PublishWeb,
    })
  }, [account?.id, organizationId])

  const trackChangelogEntryCtaClicked = useCallback(
    (
      entry: ChangelogEntry,
      cta: ChangelogCta,
      displayType: ChangelogDisplayType,
    ): void => {
      if (!account?.id || !organizationId) return

      BufferTracker.changelogEntryCTAClicked({
        userId: account.id,
        accountId: account.id,
        organizationId,
        changelogEntryId: entry.id,
        changelogEntryTitle: entry.title,
        ctaId: cta.id,
        ctaLabel: cta.label,
        ctaUrl: cta.url,
        displayType,
        clientName: Client.PublishWeb,
      })
    },
    [account?.id, organizationId],
  )

  return {
    trackChangelogEntryViewed,
    trackChangelogEntryDismissed,
    trackChangelogEntryListViewed,
    trackChangelogEntryCtaClicked,
  }
}
